// src/components/Cart/CartSummary.js
import React from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  MenuItem,
  Paper,
  Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

const CartSummary = ({ 
  cartCount,
  totalAmount,
  shippingPrice,
  originalShippingPrice,
  finalTotal,
  shippingPrices,
  selectedGovernorate,
  handleGovernorateChange,
  loadingShipping,
  paymentMethod,
  handlePaymentMethodChange,
  hasUnavailableItems,
  refreshingStock,
  cartItems,
  handleCheckout,
  clearCart,
  hasAllFreeShipping
}) => {
  // RTL TextField styling - EXACT same as ProductsFilter
  const rtlTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline legend': {
        textAlign: 'right',
        transformOrigin: 'top right',
        right: 12,
        left: 'auto'
      }
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      fontWeight: 'bold'
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 1, position: { md: 'sticky' }, top: { md: 120 }, bgcolor: 'background.paper' }}>
      {/* Order Summary Header with Blue Background */}
      <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        p: 2, 
        borderRadius: 1, 
        mb: 2,
        mx: -3,
        mt: -3
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>
          ملخص الطلب
        </Typography>
      </Box>

      {/* Free Shipping Badge */}
      {hasAllFreeShipping && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Chip
            icon={<LocalShippingIcon />}
            label="شحن مجاني لجميع المنتجات!"
            color="success"
            variant="filled"
            sx={{ 
              fontWeight: 'bold',
              fontSize: '0.9rem',
              px: 1,
              py: 0.5
            }}
          />
        </Box>
      )}
      
      {/* Governorate Selection */}
      <TextField
        select
        fullWidth
        label="المحافظة"
        value={selectedGovernorate}
        onChange={handleGovernorateChange}
        margin="normal"
        required
        disabled={loadingShipping}
        InputLabelProps={{
          sx: {
            fontWeight: 'bold',
            left: 'auto',
            '&:not(.MuiInputLabel-shrink)': {
              right: 45,   
            },
            '&.MuiInputLabel-shrink': {
              right: 1,
              transform: 'translateX(-3.5px) translateY(-7px) scale(0.75)',
            },
            '& .MuiInputLabel-asterisk': {
              display: 'none',
            },
          }
        }}
        sx={rtlTextFieldSx}
      >
        {loadingShipping ? (
          <MenuItem disabled sx={{ flexDirection: 'row-reverse', fontWeight: 'bold' }}>
            جاري التحميل...
          </MenuItem>
        ) : (
          shippingPrices.map((price) => (
            <MenuItem 
              key={price.id} 
              value={price.id}
              sx={{ flexDirection: 'row-reverse', fontWeight: 'bold' }}
            >
              {price.governorate_name_ar} 
              {hasAllFreeShipping ? 
                ' - شحن مجاني' : 
                ` - ${price.shipping_price.toFixed(2)} جنيه`
              }
            </MenuItem>
          ))
        )}
      </TextField>

      {/* Message Box under Governorate Field */}
      {!selectedGovernorate && (
        <Box sx={{ mt: 1, mb: 2, p: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
            يرجى اختيار المحافظة لإتمام الطلب
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}/>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontWeight: 'bold' }}>عدد المنتجات</Typography>
        <Typography sx={{ fontWeight: 'bold' }}>{cartCount}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontWeight: 'bold' }}>إجمالي المنتجات</Typography>
        <Typography sx={{ fontWeight: 'bold' }}>{totalAmount.toFixed(2)} جم</Typography>
      </Box>
      
      {/* Shipping Price Row with Special Handling for Free Shipping */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontWeight: 'bold' }}>رسوم الشحن</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasAllFreeShipping ? (
            <>
              {originalShippingPrice > 0 && (
                <Typography 
                  sx={{ 
                    fontWeight: 'bold', 
                    textDecoration: 'line-through', 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}
                >
                  {originalShippingPrice.toFixed(2)} جم
                </Typography>
              )}
              <Typography sx={{ fontWeight: 'bold', color: 'success.main' }}>
                مجاني
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontWeight: 'bold' }}>
              {shippingPrice.toFixed(2)} جم
            </Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontWeight: 700 }}>الإجمالي النهائي</Typography>
        <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>
          {finalTotal.toFixed(2)} جم
        </Typography>
      </Box>
      
      {/* Savings Display */}
      {hasAllFreeShipping && originalShippingPrice > 0 && (
        <Box sx={{ 
          mb: 2, 
          p: 1.5, 
          bgcolor: 'success.50', 
          borderRadius: 1, 
          border: '1px solid',
          borderColor: 'success.200'
        }}>
          <Typography variant="body2" sx={{ 
            fontWeight: 'bold', 
            color: 'success.main',
            textAlign: 'center'
          }}>
            🎉 وفرت {originalShippingPrice.toFixed(2)} جم من رسوم الشحن!
          </Typography>
        </Box>
      )}
      
      {/* Payment Method Selection */}
      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>طريقة الدفع</FormLabel>
        <RadioGroup
          aria-label="payment-method"
          name="payment-method"
          value={paymentMethod}
          onChange={handlePaymentMethodChange}
        >
          <Paper 
            variant="outlined" 
            sx={{ 
              mb: 1, 
              p: 1, 
              borderColor: paymentMethod === 'vodafone_cash' ? 'primary.main' : 'grey.300',
              borderWidth: paymentMethod === 'vodafone_cash' ? 2 : 1
            }}
          >
            <FormControlLabel 
              value="vodafone_cash" 
              control={<Radio disabled />} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ mr: 1, fontWeight: 'bold' }}>فودافون كاش</Typography>
                </Box>
              } 
            />
          </Paper>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 1, 
              borderColor: paymentMethod === 'cash_on_delivery' ? 'primary.main' : 'grey.300',
              borderWidth: paymentMethod === 'cash_on_delivery' ? 2 : 1
            }}
          >
            <FormControlLabel 
              value="cash_on_delivery" 
              control={<Radio />} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ mr: 1, fontWeight: 'bold' }}>الدفع عند الاستلام</Typography>
                </Box>
              } 
            />
          </Paper>
        </RadioGroup>
      </FormControl>
      
      {hasUnavailableItems() && (
        <Box sx={{ mb: 2, p: 1, bgcolor: '#fff4e5', borderRadius: 1 }}>
          <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
            هناك منتجات غير متوفرة في المخزون
          </Typography>
        </Box>
      )}
      

      
      <Button 
        variant="contained" 
        fullWidth 
        sx={{ mb: 1, fontWeight: 'bold' }} 
        onClick={handleCheckout}
        disabled={refreshingStock || cartItems.length === 0 || hasUnavailableItems() || !selectedGovernorate}
      >
        {refreshingStock ? 'جاري التحقق من المخزون...' : 'إتمام الشراء'}
      </Button>
      <Button variant="outlined" fullWidth color="error" onClick={clearCart} sx={{ mb: 1, fontWeight: 'bold' }}>
        إفراغ السلة
      </Button>
      <Button component={Link} to="/" fullWidth sx={{ fontWeight: 'bold' }}>استمرار التسوق</Button>
    </Paper>
  );
};

export default CartSummary;
